import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  AccountTree as AccountTreeIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import {
  useGetJobDescriptionByIdQuery,
  useCreateJobDescriptionMutation,
  useUpdateJobDescriptionMutation,
  useGetJobDescriptionDepartmentsQuery,
  useGetJobDescriptionGradesQuery,
  useGetPotentialSupervisorsQuery,
  useGetJobDescriptionReportsCountsQuery,
  useGetJobDescriptionsActiveQuery,
} from '../../redux/jobDescriptionApi';
import { useGetActiveBusinessUnitsQuery } from '../../redux/businessUnitApi';
import type {
  JobDescription,
  Qualification,
  ProfessionalRegistration,
  AuthorityLimit,
  BSCPerspective,
  KeyResultArea,
} from './bscTypes';

const DEFAULT_BSC_PERSPECTIVES: BSCPerspective[] = [
  { name: 'Financial', kras: [] },
  { name: 'Customer', kras: [] },
  { name: 'Internal Processes', kras: [] },
  { name: 'Learning & Growth', kras: [] },
];

const QUALIFICATION_LEVELS = [
  'High School Diploma',
  'Certificate',
  'Diploma',
  'Bachelor\'s Degree',
  'Honours Degree',
  'Master\'s Degree',
  'Doctoral Degree',
  'Professional Certification',
];

const AUTHORITY_CATEGORIES = [
  'Financial',
  'Procurement',
  'HR/Personnel',
  'Operational',
  'Legal/Contracts',
  'IT/Technology',
];

const CURRENCIES = ['USD', 'ZWL', 'ZAR', 'EUR', 'GBP'];

const ACCESS_LEVELS = ['Read Only', 'Read/Write', 'Admin', 'Full Access'];

const UNIT_TYPES = ['units', 'hours', 'days', 'weeks', 'projects', 'items'];

interface JobDescriptionFormProps {
  isEdit?: boolean;
}

const JobDescriptionForm: React.FC<JobDescriptionFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  // API Hooks
  const { data: existingJD, isLoading: loadingJD } = useGetJobDescriptionByIdQuery(
    { id: id! },
    { skip: !isEdit || !id }
  );
  const { data: departments = [] } = useGetJobDescriptionDepartmentsQuery();
  const { data: grades = [] } = useGetJobDescriptionGradesQuery();
  const { data: supervisors = [] } = useGetPotentialSupervisorsQuery({});
  const { data: allJobDescriptions = [] } = useGetJobDescriptionsActiveQuery();
  const { data: reportsCounts, refetch: refetchReportsCounts } = useGetJobDescriptionReportsCountsQuery(
    { id: id! },
    { skip: !id }
  );
  const { data: businessUnits = [] } = useGetActiveBusinessUnitsQuery();
  
  // Filter out current JD from supervisor options (can't report to self)
  const availableSupervisorJDs = allJobDescriptions.filter(jd => jd.id !== id);
  
  // Filter out users who are already assigned to a job description
  // Exception: If editing, include the user currently assigned to this JD
  const assignedUserIds = new Set(
    allJobDescriptions
      .filter(jd => jd.assignedUserId && jd.id !== id) // Exclude current JD when editing
      .map(jd => jd.assignedUserId)
  );
  const availableUsersForAssignment = supervisors.filter(
    user => !assignedUserIds.has(user.id)
  );
  
  const [createJD, { isLoading: creating }] = useCreateJobDescriptionMutation();
  const [updateJD, { isLoading: updating }] = useUpdateJobDescriptionMutation();
  
  // Form State
  const [formData, setFormData] = useState<Partial<JobDescription>>({
    title: '',
    grade: '',
    department: '',
    businessUnit: 'WestProp Holdings',
    jobPurpose: '',
    reportsTo: '',
    reportsToJobDescriptionId: '',
    reportsToUserId: '',
    directReportsCount: 0,
    indirectReportsCount: 0,
    limitsToAuthority: [],
    minimumQualifications: [],
    minimumJobKnowledge: [],
    minimumExperienceYears: '',
    minimumExperienceDescription: '',
    professionalRegistrations: [],
    otherRequirements: [],
    bscPerspectives: DEFAULT_BSC_PERSPECTIVES,
    requiredCompetencies: [],
    isActive: true,
  });
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newKnowledge, setNewKnowledge] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newCompetency, setNewCompetency] = useState('');
  
  // Load existing data when editing
  useEffect(() => {
    if (existingJD) {
      setFormData({
        ...existingJD,
        bscPerspectives: existingJD.bscPerspectives?.length > 0 
          ? existingJD.bscPerspectives 
          : DEFAULT_BSC_PERSPECTIVES,
      });
    }
  }, [existingJD]);
  
  // Update report counts when they're loaded
  useEffect(() => {
    if (reportsCounts) {
      setFormData(prev => ({
        ...prev,
        directReportsCount: reportsCounts.directReportsCount,
        indirectReportsCount: reportsCounts.indirectReportsCount,
      }));
    }
  }, [reportsCounts]);
  
  const handleChange = (field: keyof JobDescription, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Qualification handlers
  const addQualification = () => {
    const newQual: Qualification = {
      name: '',
      level: '',
      field: '',
      isRequired: true,
    };
    setFormData(prev => ({
      ...prev,
      minimumQualifications: [...(prev.minimumQualifications || []), newQual],
    }));
  };
  
  const updateQualification = (index: number, field: keyof Qualification, value: any) => {
    setFormData(prev => {
      const quals = [...(prev.minimumQualifications || [])];
      quals[index] = { ...quals[index], [field]: value };
      return { ...prev, minimumQualifications: quals };
    });
  };
  
  const removeQualification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      minimumQualifications: prev.minimumQualifications?.filter((_, i) => i !== index),
    }));
  };
  
  // Professional Registration handlers
  const addRegistration = () => {
    const newReg: ProfessionalRegistration = {
      body: '',
      membershipType: '',
      isRequired: true,
    };
    setFormData(prev => ({
      ...prev,
      professionalRegistrations: [...(prev.professionalRegistrations || []), newReg],
    }));
  };
  
  const updateRegistration = (index: number, field: keyof ProfessionalRegistration, value: any) => {
    setFormData(prev => {
      const regs = [...(prev.professionalRegistrations || [])];
      regs[index] = { ...regs[index], [field]: value };
      return { ...prev, professionalRegistrations: regs };
    });
  };
  
  const removeRegistration = (index: number) => {
    setFormData(prev => ({
      ...prev,
      professionalRegistrations: prev.professionalRegistrations?.filter((_, i) => i !== index),
    }));
  };
  
  // Authority Limit handlers
  const addAuthorityLimit = () => {
    const newLimit: AuthorityLimit = {
      category: '',
      description: '',
      monetaryLimit: undefined,
      currency: 'USD',
      approvalRequired: '',
    };
    setFormData(prev => ({
      ...prev,
      limitsToAuthority: [...(prev.limitsToAuthority || []), newLimit],
    }));
  };
  
  const updateAuthorityLimit = (index: number, field: keyof AuthorityLimit, value: any) => {
    setFormData(prev => {
      const limits = [...(prev.limitsToAuthority || [])];
      limits[index] = { ...limits[index], [field]: value };
      return { ...prev, limitsToAuthority: limits };
    });
  };
  
  const removeAuthorityLimit = (index: number) => {
    setFormData(prev => ({
      ...prev,
      limitsToAuthority: prev.limitsToAuthority?.filter((_, i) => i !== index),
    }));
  };
  
  // BSC KRA handlers - simplified to just add/remove string KRAs
  const addKRA = (perspectiveIndex: number, kraText: string) => {
    if (!kraText.trim()) return;
    setFormData(prev => {
      const perspectives = [...(prev.bscPerspectives || [])];
      perspectives[perspectiveIndex] = {
        ...perspectives[perspectiveIndex],
        kras: [...perspectives[perspectiveIndex].kras, kraText.trim()],
      };
      return { ...prev, bscPerspectives: perspectives };
    });
  };
  
  const removeKRA = (perspectiveIndex: number, kraIndex: number) => {
    setFormData(prev => {
      const perspectives = [...(prev.bscPerspectives || [])];
      perspectives[perspectiveIndex] = {
        ...perspectives[perspectiveIndex],
        kras: perspectives[perspectiveIndex].kras.filter((_, i) => i !== kraIndex),
      };
      return { ...prev, bscPerspectives: perspectives };
    });
  };
  
  // String array handlers
  const addToArray = (field: 'minimumJobKnowledge' | 'otherRequirements' | 'requiredCompetencies', value: string) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), value.trim()],
    }));
  };
  
  const removeFromArray = (field: 'minimumJobKnowledge' | 'otherRequirements' | 'requiredCompetencies', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index),
    }));
  };
  
  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      if (!formData.title?.trim()) {
        setError('Job Title is required');
        return;
      }
      
      // Prepare the data for submission
      const submitData = {
        ...formData,
        title: formData.title.trim(),
        isActive: formData.isActive ?? true,
        bscPerspectives: formData.bscPerspectives || DEFAULT_BSC_PERSPECTIVES,
      };
      
      console.log('Submitting Job Description:', submitData);
      
      if (isEdit && id) {
        const result = await updateJD({ id, body: submitData }).unwrap();
        console.log('Update result:', result);
        setSuccess('Job Description updated successfully!');
      } else {
        const result = await createJD(submitData as Omit<JobDescription, 'id' | 'createdAt' | 'updatedAt'>).unwrap();
        console.log('Create result:', result);
        setSuccess('Job Description created successfully!');
        setTimeout(() => navigate('/reviews/job-descriptions'), 1500);
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      const errorMessage = err?.data?.error?.message || err?.message || 'Failed to save Job Description';
      setError(errorMessage);
    }
  };
  
  if (loadingJD) {
    return (
      <PageWithHeader>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageWithHeader>
    );
  }
  
  return (
    <PageWithHeader>
      <Box sx={{ 
        maxWidth: 1200, 
        margin: '0 auto', 
        p: { xs: 2, md: 3 },
        pb: 6,
      }}>
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/reviews/job-descriptions')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {isEdit ? 'Edit Job Description' : 'Create Job Description'}
          </Typography>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {/* Job Description Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <WorkIcon color="primary" />
              <Typography variant="h6">Job Description</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Job Title"
                  value={formData.title || ''}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  freeSolo
                  options={grades}
                  value={formData.grade || ''}
                  onChange={(_, value) => handleChange('grade', value)}
                  onInputChange={(_, value) => handleChange('grade', value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Grade" placeholder="e.g., E1, M2, D3" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  freeSolo
                  options={departments}
                  value={formData.department || ''}
                  onChange={(_, value) => handleChange('department', value)}
                  onInputChange={(_, value) => handleChange('department', value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Department" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Business Unit</InputLabel>
                  <Select
                    value={formData.businessUnit || 'WestProp Holdings'}
                    label="Business Unit"
                    onChange={(e) => handleChange('businessUnit', e.target.value)}
                  >
                    {businessUnits.map((unit) => (
                      <MenuItem key={unit.id} value={unit.name}>{unit.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Job Purpose"
                  placeholder="Describe the main purpose and objectives of this role..."
                  value={formData.jobPurpose || ''}
                  onChange={(e) => handleChange('jobPurpose', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Assign To User (Optional)</InputLabel>
                  <Select
                    value={formData.assignedUserId || ''}
                    label="Assign To User (Optional)"
                    onChange={(e) => {
                      const userId = e.target.value as string;
                      handleChange('assignedUserId', userId);
                      // Also set the user's name for display
                      const user = supervisors.find(s => s.id === userId);
                      if (user) {
                        handleChange('assignedUserName', user.name || `${user.email}`);
                      } else {
                        handleChange('assignedUserName', '');
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Not assigned (auto-assign to creator)</em>
                    </MenuItem>
                    {availableUsersForAssignment.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name || user.email} {user.department ? `(${user.department})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {formData.assignedUserName && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    disabled
                    label="Assigned To"
                    value={formData.assignedUserName || ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
        
        {/* Accountability Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTreeIcon color="primary" />
              <Typography variant="h6">Accountability</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Reports To (Job Title)</InputLabel>
                  <Select
                    value={formData.reportsToJobDescriptionId || ''}
                    label="Reports To (Job Title)"
                    onChange={(e) => {
                      const jdId = e.target.value as string;
                      const supervisorJD = availableSupervisorJDs.find(j => j.id === jdId);
                      
                      handleChange('reportsToJobDescriptionId', jdId);
                      handleChange('reportsTo', supervisorJD?.title || '');
                      handleChange('reportsToUserId', supervisorJD?.assignedUserId || '');
                    }}
                  >
                    <MenuItem value="">
                      <em>None (Top Level Position)</em>
                    </MenuItem>
                    {availableSupervisorJDs.map((jd) => (
                      <MenuItem key={jd.id} value={jd.id}>
                        {jd.title} {jd.department ? `(${jd.department})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Reports To (Person)</InputLabel>
                  <Select
                    value={formData.reportsToUserId || ''}
                    label="Reports To (Person)"
                    onChange={(e) => {
                      const userId = e.target.value as string;
                      handleChange('reportsToUserId', userId);
                      
                      // Find the JD assigned to this user and auto-populate
                      const supervisorJD = availableSupervisorJDs.find(j => j.assignedUserId === userId);
                      if (supervisorJD) {
                        handleChange('reportsToJobDescriptionId', supervisorJD.id);
                        handleChange('reportsTo', supervisorJD.title);
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {availableSupervisorJDs
                      .filter(jd => jd.assignedUserId && jd.assignedUserName)
                      .map((jd) => (
                        <MenuItem key={jd.assignedUserId} value={jd.assignedUserId}>
                          {jd.assignedUserName} - {jd.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Direct Reports"
                  value={reportsCounts?.directReportsCount ?? formData.directReportsCount ?? 0}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment>,
                  }}
                  helperText="Auto-calculated from JD hierarchy"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Indirect Reports"
                  value={reportsCounts?.indirectReportsCount ?? formData.indirectReportsCount ?? 0}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment>,
                  }}
                  helperText="Total under direct reports"
                />
              </Grid>
              
              {/* Limits to Authority */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">Limits to Authority</Typography>
                  <Button startIcon={<AddIcon />} onClick={addAuthorityLimit} size="small">
                    Add Limit
                  </Button>
                </Box>
                {formData.limitsToAuthority?.map((limit, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={limit.category}
                            label="Category"
                            onChange={(e) => updateAuthorityLimit(index, 'category', e.target.value)}
                          >
                            {AUTHORITY_CATEGORIES.map((cat) => (
                              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={limit.category ? 6 : 8}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          placeholder="Describe the scope of authority..."
                          value={limit.description}
                          onChange={(e) => updateAuthorityLimit(index, 'description', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={2} md={1}>
                        <IconButton color="error" onClick={() => removeAuthorityLimit(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                      
                      {/* Category-specific fields */}
                      {(limit.category === 'Financial' || limit.category === 'Procurement') && (
                        <>
                          <Grid item xs={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Monetary Limit"
                              value={limit.monetaryLimit || ''}
                              onChange={(e) => updateAuthorityLimit(index, 'monetaryLimit', parseFloat(e.target.value) || undefined)}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Currency</InputLabel>
                              <Select
                                value={limit.currency || 'USD'}
                                label="Currency"
                                onChange={(e) => updateAuthorityLimit(index, 'currency', e.target.value)}
                              >
                                {CURRENCIES.map((c) => (
                                  <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      
                      {limit.category === 'HR/Personnel' && (
                        <>
                          <Grid item xs={6} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Headcount Limit"
                              value={limit.headcountLimit || ''}
                              onChange={(e) => updateAuthorityLimit(index, 'headcountLimit', parseInt(e.target.value) || undefined)}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Salary Band Limit"
                              placeholder="e.g., Up to Grade D3"
                              value={limit.salaryBandLimit || ''}
                              onChange={(e) => updateAuthorityLimit(index, 'salaryBandLimit', e.target.value)}
                            />
                          </Grid>
                        </>
                      )}
                      
                      {limit.category === 'Operational' && (
                        <>
                          <Grid item xs={6} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Unit Limit"
                              value={limit.unitLimit || ''}
                              onChange={(e) => updateAuthorityLimit(index, 'unitLimit', parseInt(e.target.value) || undefined)}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Unit Type</InputLabel>
                              <Select
                                value={limit.unitType || ''}
                                label="Unit Type"
                                onChange={(e) => updateAuthorityLimit(index, 'unitType', e.target.value)}
                              >
                                {UNIT_TYPES.map((u) => (
                                  <MenuItem key={u} value={u}>{u}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      
                      {limit.category === 'Legal/Contracts' && (
                        <>
                          <Grid item xs={6} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Duration Limit"
                              placeholder="e.g., Up to 2 years"
                              value={limit.contractDurationLimit || ''}
                              onChange={(e) => updateAuthorityLimit(index, 'contractDurationLimit', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Contract Value Limit"
                              value={limit.contractValueLimit || ''}
                              onChange={(e) => updateAuthorityLimit(index, 'contractValueLimit', parseFloat(e.target.value) || undefined)}
                            />
                          </Grid>
                        </>
                      )}
                      
                      {limit.category === 'IT/Technology' && (
                        <>
                          <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Access Level</InputLabel>
                              <Select
                                value={limit.accessLevel || ''}
                                label="Access Level"
                                onChange={(e) => updateAuthorityLimit(index, 'accessLevel', e.target.value)}
                              >
                                {ACCESS_LEVELS.map((a) => (
                                  <MenuItem key={a} value={a}>{a}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="System Scope"
                              placeholder="e.g., ERP, CRM, All Systems"
                              value={limit.systemScope || ''}
                              onChange={(e) => updateAuthorityLimit(index, 'systemScope', e.target.value)}
                            />
                          </Grid>
                        </>
                      )}
                      
                      {/* Common approval field - show when category is selected */}
                      {limit.category && (
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Approval Required Beyond Limit"
                            placeholder="e.g., CEO, Board, Department Head"
                            value={limit.approvalRequired || ''}
                            onChange={(e) => updateAuthorityLimit(index, 'approvalRequired', e.target.value)}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                ))}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        
        {/* Inherent Job Requirements Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <SchoolIcon color="primary" />
              <Typography variant="h6">Inherent Job Requirements</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Minimum Qualifications */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">Minimum Qualifications</Typography>
                <Button startIcon={<AddIcon />} onClick={addQualification} size="small">
                  Add Qualification
                </Button>
              </Box>
              {formData.minimumQualifications?.map((qual, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Qualification Name"
                        placeholder="e.g., Bachelor of Commerce"
                        value={qual.name}
                        onChange={(e) => updateQualification(index, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Level</InputLabel>
                        <Select
                          value={qual.level || ''}
                          label="Level"
                          onChange={(e) => updateQualification(index, 'level', e.target.value)}
                        >
                          {QUALIFICATION_LEVELS.map((level) => (
                            <MenuItem key={level} value={level}>{level}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Field of Study"
                        placeholder="e.g., Finance, IT"
                        value={qual.field || ''}
                        onChange={(e) => updateQualification(index, 'field', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={10} md={1}>
                      <Chip
                        label={qual.isRequired ? 'Required' : 'Preferred'}
                        color={qual.isRequired ? 'primary' : 'default'}
                        size="small"
                        onClick={() => updateQualification(index, 'isRequired', !qual.isRequired)}
                      />
                    </Grid>
                    <Grid item xs={2} md={1}>
                      <IconButton color="error" onClick={() => removeQualification(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Minimum Job Knowledge */}
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>Minimum Job-Related Knowledge</Typography>
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add knowledge area..."
                  value={newKnowledge}
                  onChange={(e) => setNewKnowledge(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addToArray('minimumJobKnowledge', newKnowledge);
                      setNewKnowledge('');
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    addToArray('minimumJobKnowledge', newKnowledge);
                    setNewKnowledge('');
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.minimumJobKnowledge?.map((knowledge, index) => (
                  <Chip
                    key={index}
                    label={knowledge}
                    onDelete={() => removeFromArray('minimumJobKnowledge', index)}
                  />
                ))}
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Experience */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Minimum Experience"
                  placeholder="e.g., 5+ years"
                  value={formData.minimumExperienceYears || ''}
                  onChange={(e) => handleChange('minimumExperienceYears', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Experience Description"
                  placeholder="Describe the type of experience required..."
                  value={formData.minimumExperienceDescription || ''}
                  onChange={(e) => handleChange('minimumExperienceDescription', e.target.value)}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Professional Registrations */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">Professional Registrations & Affiliations</Typography>
                <Button startIcon={<AddIcon />} onClick={addRegistration} size="small">
                  Add Registration
                </Button>
              </Box>
              {formData.professionalRegistrations?.map((reg, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Professional Body"
                        placeholder="e.g., CIMA, ACCA, PMP"
                        value={reg.body}
                        onChange={(e) => updateRegistration(index, 'body', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Membership Type"
                        placeholder="e.g., Full Member, Associate"
                        value={reg.membershipType || ''}
                        onChange={(e) => updateRegistration(index, 'membershipType', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={10} md={3}>
                      <Chip
                        label={reg.isRequired ? 'Required' : 'Preferred'}
                        color={reg.isRequired ? 'primary' : 'default'}
                        size="small"
                        onClick={() => updateRegistration(index, 'isRequired', !reg.isRequired)}
                      />
                    </Grid>
                    <Grid item xs={2} md={1}>
                      <IconButton color="error" onClick={() => removeRegistration(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Other Requirements */}
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>Other Job Requirements</Typography>
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add requirement (e.g., Driver's license, Travel required)..."
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addToArray('otherRequirements', newRequirement);
                      setNewRequirement('');
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    addToArray('otherRequirements', newRequirement);
                    setNewRequirement('');
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.otherRequirements?.map((req, index) => (
                  <Chip
                    key={index}
                    label={req}
                    onDelete={() => removeFromArray('otherRequirements', index)}
                  />
                ))}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* BSC Perspectives Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6">BSC Perspectives & Key Result Areas</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Define Key Result Areas (KRAs) for each BSC perspective. These will be used during performance reviews.
            </Typography>
            
            <Grid container spacing={3}>
              {formData.bscPerspectives?.map((perspective, pIndex) => (
                <Grid item xs={12} md={6} key={pIndex}>
                  <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={2}>
                      {perspective.name}
                    </Typography>
                    
                    <Box display="flex" gap={1} mb={2}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`Add KRA for ${perspective.name}...`}
                        id={`kra-input-${pIndex}`}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const input = e.target as HTMLInputElement;
                            if (input.value.trim()) {
                              addKRA(pIndex, input.value.trim());
                              input.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const input = document.getElementById(`kra-input-${pIndex}`) as HTMLInputElement;
                          if (input && input.value.trim()) {
                            addKRA(pIndex, input.value.trim());
                            input.value = '';
                          }
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                    
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {perspective.kras.map((kra, kIndex) => (
                        <Chip
                          key={kIndex}
                          label={kra}
                          color="primary"
                          variant="outlined"
                          onDelete={() => removeKRA(pIndex, kIndex)}
                          sx={{ maxWidth: '100%' }}
                        />
                      ))}
                    </Box>
                    
                    {perspective.kras.length === 0 && (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No KRAs added yet
                      </Typography>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {/* Required Competencies */}
            <Box mt={3}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>Required Competencies</Typography>
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add competency..."
                  value={newCompetency}
                  onChange={(e) => setNewCompetency(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addToArray('requiredCompetencies', newCompetency);
                      setNewCompetency('');
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    addToArray('requiredCompetencies', newCompetency);
                    setNewCompetency('');
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.requiredCompetencies?.map((comp, index) => (
                  <Chip
                    key={index}
                    label={comp}
                    color="primary"
                    variant="outlined"
                    onDelete={() => removeFromArray('requiredCompetencies', index)}
                  />
                ))}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* Action Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={3} sx={{ 
          position: 'sticky', 
          bottom: 0, 
          bgcolor: 'background.paper', 
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          mx: -3,
          px: 3,
        }}>
          <Button variant="outlined" onClick={() => navigate('/reviews/job-descriptions')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={creating || updating}
          >
            {creating || updating ? <CircularProgress size={24} color="inherit" /> : isEdit ? 'Update' : 'Create'}
          </Button>
        </Box>
        </Paper>
      </Box>
    </PageWithHeader>
  );
};

export default JobDescriptionForm;
